import { createStore } from "zustand/vanilla";
import type { NotificationsRepository } from "@/api/notifications/repository";
import type { IStreamNotificationsResponse } from "@/api/notifications/types";
import type { INotification } from "@/types/notifications";

export type INotificationsModelState = {
  items: INotification[];
  unreadCount: number;
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
  /** Last page failed: auto-loading stops until the feed is reset. */
  error: boolean;
};

type INotificationsRepository = Pick<
  NotificationsRepository,
  | "getAll"
  | "getUnreadCount"
  | "markAsRead"
  | "markAllAsRead"
  | "delete"
  | "deleteAll"
  | "stream"
>;

export type INotificationsCenterDeps = {
  repository: INotificationsRepository;
  getAccessToken: () => string;
  pageSize: number;
};

const MAX_RECONNECT_DELAY = 30_000;

const initialState: INotificationsModelState = {
  items: [],
  unreadCount: 0,
  hasMore: true,
  loading: false,
  loaded: false,
  error: false,
};

/** Appends a page and drops ids already in the list (pages shift when new items arrive). */
const mergeById = (items: INotification[], page: INotification[]) => {
  const ids = new Set(items.map((n) => n.id));
  return items.concat(page.filter((n) => !ids.has(n.id)));
};

/**
 * Notifications domain model, free of React.
 * Owns the unread counter (live stream), the paginated feed and the
 * optimistic actions. UI reads `store` and calls the methods.
 */
export class NotificationsCenter {
  readonly store = createStore<INotificationsModelState>()(() => initialState);

  private tenant: string | null = null;
  private stream: IStreamNotificationsResponse | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  // Bumped on start/stop: async work from an older session must not touch state
  private session = 0;

  private feedController: AbortController | null = null;
  // Bumped on resetFeed: a page that lands after a reset is dropped
  private feed = 0;

  constructor(private readonly deps: INotificationsCenterDeps) {}

  /* ---------- Live unread counter ---------- */

  start(tenant: string) {
    if (this.tenant === tenant) return;

    this.stop();
    this.tenant = tenant;
    this.connect(this.session);
  }

  stop() {
    this.session += 1;
    this.tenant = null;
    this.reconnectAttempt = 0;
    this.stream?.close();
    this.stream = null;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Syncs the counter through axios first: that call refreshes an expired
   * token, and it covers events missed while disconnected.
   */
  private connect(session: number) {
    this.deps.repository
      .getUnreadCount()
      .then((unreadCount) => {
        if (session !== this.session || !this.tenant) return;

        this.store.setState({ unreadCount });
        this.stream = this.deps.repository.stream({
          tenant: this.tenant,
          accessToken: this.deps.getAccessToken(),
          onUnreadCount: (count) => {
            this.reconnectAttempt = 0;
            this.store.setState({ unreadCount: count });
          },
          onClose: () => this.scheduleReconnect(session),
        });
      })
      .catch(() => this.scheduleReconnect(session));
  }

  private scheduleReconnect(session: number) {
    if (session !== this.session) return;

    // 1s, 2s, 4s… capped: a dead server is not hammered, a clean close reconnects fast
    const delay = Math.min(
      MAX_RECONNECT_DELAY,
      1_000 * 2 ** this.reconnectAttempt,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(session), delay);
  }

  /* ---------- Feed ---------- */

  /** Loads the next page. Rejects only on a real server error. */
  async loadMore() {
    const { loading, hasMore, error, items } = this.store.getState();
    if (loading || !hasMore || error) return;

    const feed = this.feed;
    const { pageSize } = this.deps;
    // Page from the loaded count, not a counter: after a delete the server
    // pages shift back, so the page is refetched and deduped instead of skipped
    const page = Math.floor(items.length / pageSize) + 1;

    this.feedController = new AbortController();
    this.store.setState({ loading: true });

    try {
      const response = await this.deps.repository.getAll(
        { page, size: pageSize },
        { signal: this.feedController.signal },
      );
      if (feed !== this.feed) return;

      this.store.setState((s) => ({
        items: mergeById(s.items, response),
        hasMore: response.length === pageSize,
        loaded: true,
        loading: false,
      }));
    } catch (error) {
      if (feed !== this.feed) return;

      this.store.setState({ loading: false, loaded: true, error: true });
      throw error;
    }
  }

  /** Drops the feed (panel closed). The counter keeps running. */
  resetFeed() {
    this.feed += 1;
    this.feedController?.abort();
    this.feedController = null;
    this.store.setState({
      items: [],
      hasMore: true,
      loading: false,
      loaded: false,
      error: false,
    });
  }

  /* ---------- Actions: optimistic, rolled back on failure ---------- */

  async markAsRead(id: number) {
    const item = this.findItem(id);
    if (!item || item.isRead) return;

    this.patchItem(id, { isRead: true }, -1);

    try {
      await this.deps.repository.markAsRead({ id });
    } catch (error) {
      this.patchItem(id, { isRead: false }, +1);
      throw error;
    }
  }

  async remove(id: number) {
    const { items } = this.store.getState();
    const index = items.findIndex((n) => n.id === id);
    if (index === -1) return;

    const item = items[index];
    const countDelta = item.isRead ? 0 : 1;

    this.store.setState((s) => ({
      items: s.items.filter((n) => n.id !== id),
      unreadCount: Math.max(0, s.unreadCount - countDelta),
    }));

    try {
      await this.deps.repository.delete({ id });
    } catch (error) {
      // Put it back where it was, unless something else already restored it
      this.store.setState((s) => {
        if (s.items.some((n) => n.id === id)) return s;
        const restored = [...s.items];
        restored.splice(Math.min(index, restored.length), 0, item);
        return {
          items: restored,
          unreadCount: s.unreadCount + countDelta,
        };
      });
      throw error;
    }
  }

  async markAllAsRead() {
    const { items, unreadCount } = this.store.getState();
    const unreadIds = new Set(items.filter((n) => !n.isRead).map((n) => n.id));

    this.store.setState((s) => ({
      items: s.items.map((n) => (n.isRead ? n : { ...n, isRead: true })),
      unreadCount: 0,
    }));

    try {
      await this.deps.repository.markAllAsRead();
    } catch (error) {
      this.store.setState((s) => ({
        items: s.items.map((n) =>
          unreadIds.has(n.id) ? { ...n, isRead: false } : n,
        ),
        unreadCount,
      }));
      throw error;
    }
  }

  async removeAll() {
    const { items, unreadCount, hasMore } = this.store.getState();

    this.store.setState({ items: [], unreadCount: 0, hasMore: false });

    try {
      await this.deps.repository.deleteAll();
    } catch (error) {
      this.store.setState({ items, unreadCount, hasMore });
      throw error;
    }
  }

  /** Full teardown on sign-out. */
  reset() {
    this.stop();
    this.resetFeed();
    this.store.setState(initialState);
  }

  private findItem(id: number) {
    return this.store.getState().items.find((n) => n.id === id);
  }

  private patchItem(
    id: number,
    patch: Partial<INotification>,
    countDelta: number,
  ) {
    this.store.setState((s) => ({
      items: s.items.map((n) => (n.id === id ? { ...n, ...patch } : n)),
      unreadCount: Math.max(0, s.unreadCount + countDelta),
    }));
  }
}
